import React, { useContext, useEffect, useState } from "react";
import HoverTooltip from "../common/HoverTooltip";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import { toast } from "../../utils/toast";
import { getAutoAssignStatus, updateAutoAssignStatus } from "../../service/User";
import { Zap } from "lucide-react";

/**
 * Project-level Auto Assign enable/disable control.
 * Uses GET/POST /api/v1/project/*-auto-assign-status with projectId.
 */
const ProjectAutoAssignToggle = ({ disabled = false, className = "", wrapperClassName = "", tooltipPosition = "top" }) => {
  const { projectId, langMode, theme } = useContext(AppContext);
  const isRtl = langMode === "ar" || langMode === "ur";
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadStatus = async () => {
      if (!projectId) {
        setEnabled(false);
        return;
      }
      setLoading(true);
      try {
        const status = await getAutoAssignStatus(projectId);
        if (!cancelled) setEnabled(status);
      } catch (error) {
        if (!cancelled) {
          setEnabled(false);
          toast.error(
            translations[langMode]?.failedToLoadAutoAssign ??
              "Failed to load auto-assign status."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadStatus();
    return () => {
      cancelled = true;
    };
  }, [projectId, langMode]);

  const handleToggle = async () => {
    if (!projectId || disabled || loading || updating) return;
    const next = !enabled;
    const previous = enabled;
    setEnabled(next);
    setUpdating(true);
    try {
      const response = await updateAutoAssignStatus(projectId, next);
      if (response?.header?.code === 600) {
        toast.success(
          response?.body?.value ??
            translations[langMode]?.statusupdatedsucc ??
            "Status updated successfully."
        );
      } else {
        setEnabled(previous);
        toast.error(
          response?.body?.value ??
            response?.body?.error ??
            translations[langMode]?.failedToUpdateAutoAssign ??
            "Failed to update auto-assign status."
        );
      }
    } catch (error) {
      setEnabled(previous);
      const message =
        error?.response?.data?.body?.value ??
        error?.response?.data?.body?.error ??
        error?.message;
      toast.error(
        message ||
          translations[langMode]?.failedToUpdateAutoAssign ||
          "Failed to update auto-assign status."
      );
    } finally {
      setUpdating(false);
    }
  };

  const isBusy = loading || updating || !projectId;
  const label =
    translations[langMode]?.autoAssign ??
    translations[langMode]?.autoAssignTask ??
    "Auto Assign";
  const tooltipText = translations[langMode]?.autoasigntaskto ?? label;

  return (
    <div className={`relative inline-block ${wrapperClassName}`.trim()}>
      <HoverTooltip text={tooltipText} position={tooltipPosition}>
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled || isBusy}
        className={`inline-flex h-10 min-h-[40px] max-h-10 items-center gap-2 rounded-lg border px-3 text-xs font-medium transition-colors sm:px-4 sm:text-sm ${
          enabled
            ? "border-[#45B369] bg-[#45B369] text-white hover:bg-[#3aa05b]"
            : theme === "dark"
              ? "border-gray-600 bg-gray-700 text-gray-100 hover:bg-gray-600"
              : "border-[#45B369] bg-[#45B369] text-white hover:bg-[#3aa05b]"
        } ${disabled || isBusy ? "cursor-not-allowed opacity-50" : "cursor-pointer"} ${className}`}
      >
        <Zap size={18} className={"text-white"} />
        <span>{label}</span>
        <span
          className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${
            enabled ? "bg-white/30" : "bg-gray-300"
          }`}
          aria-hidden="true"
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${
              enabled
                ? isRtl
                  ? "-translate-x-4"
                  : "translate-x-4"
                : "translate-x-0.5"
            }`}
          />
        </span>
      </button>
      </HoverTooltip>
    </div>
  );
};

export default ProjectAutoAssignToggle;
